import { PUZZLES } from '../data/puzzles'
import type { Puzzle, GameState, ShareResult } from '../types/puzzle'

export * from '../types/puzzle'

/** 游戏发布日期（用于计算天数） */


/**
 * 获取今日日期字符串
 * @returns {string} YYYY-MM-DD 格式的日期
 */
export function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * 计算谜题天数编号 (改为随机，以便每次刷新都不同)
 * @returns {number} 随机天数编号
 */
export function getDayNumber(): number {
  // 生成一个 1 到 10000 之间的随机数，模拟“第N天”
  // 这样每次刷新首页或进入每日页面，都会拿到不同的编号
  return Math.floor(Math.random() * 10000) + 1
}

/**
 * Get puzzle for the current day
 */
export function getDailyPuzzle(dayNumber?: number): Puzzle {
  const day = dayNumber ?? getDayNumber()
  // Ensure we have puzzles
  if (!PUZZLES || PUZZLES.length === 0) {
    throw new Error('No puzzles available') // Should fail gracefully in UI ideally
  }

  // Rotation logic
  const index = day % PUZZLES.length
  const template = PUZZLES[index]

  return {
    ...template,
    dayNumber: day,
    // Keep title but append day number if generic? Or just rely on template.
    // For "Different words", the puzzles themselves are distinct.
    id: `daily-${day}`
  }
}

/**
 * 创建空的用户输入网格
 * @param {number} size - 网格大小
 * @returns {string[][]} 空网格
 */
export function createEmptyGrid(size: number): string[][] {
  return Array(size).fill(null).map(() => Array(size).fill(''))
}

/**
 * 检查是否为黑格
 * @param {[number, number][]} blocked - 黑格列表
 * @param {number} row - 行号
 * @param {number} col - 列号
 * @returns {boolean} 是否为黑格
 */
export function isBlockedCell(
  blocked: [number, number][],
  row: number,
  col: number
): boolean {
  return blocked.some(([r, c]) => r === row && c === col)
}

/**
 * 检查用户答案是否全部正确
 * @param {string[][]} userInput - 用户输入
 * @param {string[][]} answer - 正确答案
 * @param {[number, number][]} blocked - 黑格列表
 * @returns {boolean} 是否全部正确
 */
export function checkAllCorrect(
  userInput: string[][],
  answer: string[][],
  blocked: [number, number][]
): boolean {
  for (let row = 0; row < answer.length; row++) {
    for (let col = 0; col < answer[row].length; col++) {
      if (isBlockedCell(blocked, row, col)) continue
      if (userInput[row][col].toUpperCase() !== answer[row][col].toUpperCase()) {
        return false
      }
    }
  }
  return true
}

/**
 * 获取下一个单元格位置
 * @param {[number, number]} current - 当前位置
 * @param {'across' | 'down'} direction - 方向
 * @param {Puzzle} puzzle - 谜题数据
 * @returns {[number, number]} 下一个位置
 */
export function getNextCell(
  current: [number, number],
  direction: 'across' | 'down',
  puzzle: Puzzle
): [number, number] {
  const [row, col] = current
  const { size, blocked } = puzzle.grid

  let nextRow = row
  let nextCol = col

  if (direction === 'across') {
    nextCol = col + 1
    // 换行
    if (nextCol >= size) {
      nextCol = 0
      nextRow = row + 1
    }
  } else {
    nextRow = row + 1
    // 换列
    if (nextRow >= size) {
      nextRow = 0
      nextCol = col + 1
    }
  }

  // 边界检查
  if (nextRow >= size || nextCol >= size) {
    return current
  }

  // 跳过黑格
  if (isBlockedCell(blocked, nextRow, nextCol)) {
    return getNextCell([nextRow, nextCol], direction, puzzle)
  }

  return [nextRow, nextCol]
}

/**
 * 格式化时间
 * @param {number} milliseconds - 毫秒数
 * @returns {string} 格式化的时间字符串 M:SS
 */
export function formatTime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')} `
}

/**
 * 生成 Wordle 风格的分享文本
 * @param {GameState} state - 游戏状态
 * @param {Puzzle} puzzle - 谜题数据
 * @returns {ShareResult} 分享结果
 */
export function generateShareResult(
  state: GameState,
  puzzle: Puzzle
): ShareResult {
  const { userInput, startTime, endTime, hintsUsed } = state
  const { grid, dayNumber } = puzzle

  // 计算用时
  const duration = (endTime || Date.now()) - startTime
  const timeStr = formatTime(duration)

  // 生成 emoji 网格
  const emojiGrid = userInput.map((row, rowIdx) =>
    row.map((cell, colIdx) => {
      // 黑格
      if (isBlockedCell(grid.blocked, rowIdx, colIdx)) {
        return '⬛'
      }
      // 正确
      if (cell.toUpperCase() === grid.cells[rowIdx][colIdx].toUpperCase()) {
        return '🟩'
      }
      // 空或错误
      return '⬜'
    }).join('')
  ).join('\n')

  return {
    emojiGrid,
    timeStr,
    dayNumber: dayNumber || 0,
    hintsUsed
  }
}

/**
 * 生成完整的分享文本
 * @param {ShareResult} result - 分享结果
 * @param {string} gameUrl - 游戏链接
 * @returns {string} 完整的分享文本
 */
export function generateShareText(
  result: ShareResult,
  gameUrl: string = 'https://blazegrids.netherfire.com'
): string {
  let text = `Blaze Grids #${result.dayNumber} ${result.timeStr} \n\n`
  text += result.emojiGrid

  if (result.hintsUsed > 0) {
    text += `\n\n💡 ${result.hintsUsed} hint${result.hintsUsed > 1 ? 's' : ''} `
  }

  text += `\n\n🔗 ${gameUrl} `

  return text
}

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @returns {Promise<boolean>} 是否成功
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // 降级方案
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  }
}

/**
 * 本地持久化的游戏统计数据结构
 * 注意：这是全项目唯一的本地游戏进度存储，成就系统（见 src/lib/achievements.ts）
 * 直接复用 `unlockedAchievements` 字段，不另起新的 localStorage key。
 */
export interface GameStats {
  /** 累计开始/结束的对局数（含超时失败） */
  gamesPlayed: number
  /** 累计在倒计时内解完的对局数 */
  gamesWon: number
  /** 当前连续完成每日谜题的自然日数 */
  currentStreak: number
  /** 历史最长连续自然日数 */
  maxStreak: number
  /** 胜利对局的平均用时（秒） */
  avgTime: number
  /** 最佳用时（秒），尚无记录时为 null */
  bestTime: number | null
  /** 累计「全程无提示」解完的对局数 */
  noHintWins: number
  /** 最近一次记为「胜利」的自然日日期字符串（YYYY-MM-DD），用于连续天数计算 */
  lastPlayedDateStr: string | null
  /** 上一局是否以「超时失败」结束（用于 from-the-ashes 隐藏成就） */
  lastGameTimedOut: boolean
  /** 已解锁的成就 key 列表（见 AchievementKey），用于本地去重，避免重复上报 */
  unlockedAchievements: string[]
}

const DEFAULT_GAME_STATS: GameStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  currentStreak: 0,
  maxStreak: 0,
  avgTime: 0,
  bestTime: null,
  noHintWins: 0,
  lastPlayedDateStr: null,
  lastGameTimedOut: false,
  unlockedAchievements: []
}

/**
 * 从本地存储加载游戏统计
 * 与旧版本兼容：缺失的字段会用默认值补齐
 * @returns {GameStats} 游戏统计数据
 */
export function loadGameStats(): GameStats {
  try {
    const raw = localStorage.getItem('blazegrids_stats')
    if (!raw) return { ...DEFAULT_GAME_STATS }
    const parsed = JSON.parse(raw)
    return {
      ...DEFAULT_GAME_STATS,
      ...parsed,
      unlockedAchievements: Array.isArray(parsed.unlockedAchievements) ? parsed.unlockedAchievements : []
    }
  } catch {
    return { ...DEFAULT_GAME_STATS }
  }
}

/**
 * 保存游戏统计到本地存储
 * @param {GameStats} stats - 统计数据
 */
export function saveGameStats(stats: GameStats): void {
  try {
    localStorage.setItem('blazegrids_stats', JSON.stringify(stats))
  } catch (error) {
    console.error('Failed to save game stats', error)
  }
}

/**
 * 判断 dateStr 是否恰好是 todayStr 的前一个自然日
 */
function isPreviousCalendarDay(dateStr: string, todayStr: string): boolean {
  const prev = new Date(`${dateStr}T00:00:00`)
  const today = new Date(`${todayStr}T00:00:00`)
  if (Number.isNaN(prev.getTime()) || Number.isNaN(today.getTime())) return false
  const diffDays = Math.round((today.getTime() - prev.getTime()) / 86400000)
  return diffDays === 1
}

/**
 * 单局游戏结果输入
 */
export interface GameResultInput {
  /** 是否在倒计时结束前解完 */
  won: boolean
  /** 本局用时（毫秒），仅在 won 为 true 时有意义 */
  durationMs: number
  /** 本局使用的提示次数 */
  hintsUsed: number
}

/**
 * 记录单局结果后的统计结果，供成就判定使用
 */
export interface GameResultOutcome {
  /** 更新后的统计数据 */
  stats: GameStats
  /** 记录本局之前，玩家是否已经至少胜利过一次（用于“首胜后再…”类成就） */
  hasPriorVictory: boolean
  /** 上一局是否以超时失败结束，且本局是胜利（用于 from-the-ashes 隐藏成就） */
  cameBackFromTimeout: boolean
}

/**
 * 记录一局游戏的结果（胜利或超时失败），更新累计次数 / 连续天数 / 最佳用时等统计。
 * 每局游戏应且仅应调用一次（由调用方通过 ref 等方式保证幂等）。
 * @param {GameResultInput} input - 本局结果
 * @returns {GameResultOutcome} 更新后的统计数据及成就判定所需的上下文
 */
export function recordGameResult(input: GameResultInput): GameResultOutcome {
  const stats = loadGameStats()
  const hasPriorVictory = stats.gamesWon > 0
  const cameBackFromTimeout = input.won && stats.lastGameTimedOut

  stats.gamesPlayed += 1

  if (input.won) {
    const durationSec = Math.round(input.durationMs / 1000)

    stats.avgTime = stats.gamesWon === 0
      ? durationSec
      : Math.round((stats.avgTime * stats.gamesWon + durationSec) / (stats.gamesWon + 1))
    stats.gamesWon += 1
    stats.bestTime = stats.bestTime === null ? durationSec : Math.min(stats.bestTime, durationSec)

    if (input.hintsUsed === 0) {
      stats.noHintWins += 1
    }

    const today = getTodayDateString()
    if (stats.lastPlayedDateStr !== today) {
      stats.currentStreak = isPreviousCalendarDay(stats.lastPlayedDateStr ?? '', today)
        ? stats.currentStreak + 1
        : 1
      stats.lastPlayedDateStr = today
    }
    stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak)
    stats.lastGameTimedOut = false
  } else {
    stats.lastGameTimedOut = true
  }

  saveGameStats(stats)

  return { stats, hasPriorVictory, cameBackFromTimeout }
}


/**
 * Clue information derived from grid structure
 */
export interface GridClueInfo {
  direction: 'across' | 'down'
  number: number
  row: number
  col: number
  length: number
}

/**
 * Derived grid numbers and required clues based on blocked cells
 * @param {number} size - Grid size (e.g. 5)
 * @param {Set<string>} blocked - Set of blocked keys "r-c"
 * @returns {object} cellNumbers grid and list of clue placeholders
 */
export function deriveCluesFromGrid(
  size: number,
  blocked: Set<string>
): { cellNumbers: number[][], clueInfos: GridClueInfo[] } {
  const cellNumbers = Array(size).fill(null).map(() => Array(size).fill(0))
  const clueInfos: GridClueInfo[] = []

  let currentNumber = 1

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (blocked.has(`${r} -${c} `)) continue

      let isStart = false

      // Check Across Start
      // Must be (left edge OR prev is blocked) AND (has valid next cell making length >= 1... actually crossword convention is complex, 
      // but usually we number any start. Standard crosswords require words >= 3, but 5x5 minis allow 2-letter words sometimes.)
      // Simplest rule: Number if it's the start of a word > 1 letter, OR if it's a valid 1-letter word (rare).
      // Let's stick to standard numbering: Number if it has neighbors to right or bottom? 
      // No, basic rule: If top-most or left-most of a sequence, it gets a number.

      const isLeftEdge = c === 0 || blocked.has(`${r} -${c - 1} `)
      const isTopEdge = r === 0 || blocked.has(`${r - 1} -${c} `)

      const hasRight = c + 1 < size && !blocked.has(`${r} -${c + 1} `)
      const hasBottom = r + 1 < size && !blocked.has(`${r + 1} -${c} `)

      // A cell needs a number if it starts an Across word OR a Down word.
      // An Across word exists if it has a cell to the right? Or just isolated 1-cell word?
      // Let's assume minimum word length 2 for "word-ness", but conventionally number is assigned if it's a start of sequence.

      if (isLeftEdge && hasRight) {
        isStart = true
        // Calculate length
        let len = 1
        while (c + len < size && !blocked.has(`${r} -${c + len} `)) {
          len++
        }
        clueInfos.push({
          direction: 'across',
          number: currentNumber,
          row: r,
          col: c,
          length: len
        })
      }

      if (isTopEdge && hasBottom) {
        isStart = true
        // Calculate length
        let len = 1
        while (r + len < size && !blocked.has(`${r + len} -${c} `)) {
          len++
        }
        clueInfos.push({
          direction: 'down',
          number: currentNumber,
          row: r,
          col: c,
          length: len
        })
      }

      if (isStart) {
        cellNumbers[r][c] = currentNumber
        currentNumber++
      }
    }
  }

  return { cellNumbers, clueInfos }
}
